import * as XLSX from 'xlsx';

/**
 * Export micronutrient data to Excel
 * @param {Object} data - Data to export
 * @param {Object} data.patient - Patient information
 * @param {string} data.date - Calculation date
 * @param {number} data.targetEnergy - Target energy
 * @param {Object} data.micronutrients - Micronutrient data (oral and parenteral)
 * @param {Object} data.sodiumMeq - Sodium in mEq
 * @param {Object} data.potassiumMeq - Potassium in mEq
 * @param {string} filename - Output filename
 */
export const exportMicronutrientsToExcel = (data, filename) => {
  const { patient, date, targetEnergy, micronutrients, sodiumMeq, potassiumMeq } = data;

  // Create a new workbook
  const wb = XLSX.utils.book_new();

  // Prepare data array for Excel sheet
  const sheetData = [];

  // Header Section
  sheetData.push(['NUTRIMUS - Macro & Micronutrient Calculation Report']);
  sheetData.push([]);
  
  // Patient Information
  sheetData.push(['PATIENT INFORMATION']);
  sheetData.push(['Name:', patient?.name || '-']);
  sheetData.push(['Gender:', patient?.gender === 'male' ? 'Male' : patient?.gender === 'female' ? 'Female' : '-']);
  sheetData.push(['Age:', patient?.age ? `${patient.age} years` : '-']);
  sheetData.push(['Date:', date || '-']);
  sheetData.push(['Target Energy:', `${targetEnergy} KKal`]);
  sheetData.push([]);

  // Macronutrient Summary
  sheetData.push(['MACRONUTRIENT SUMMARY']);
  sheetData.push(['Nutrient', 'Oral/Enteral', 'Parenteral', 'Total', 'Unit']);
  sheetData.push([
    'Energy',
    micronutrients.oral.energy_kcal.toFixed(2),
    micronutrients.parenteral.energy_kcal.toFixed(2),
    (micronutrients.oral.energy_kcal + micronutrients.parenteral.energy_kcal).toFixed(2),
    'KKal'
  ]);
  sheetData.push([
    'Protein',
    micronutrients.oral.protein_g.toFixed(2),
    micronutrients.parenteral.protein_g.toFixed(2),
    (micronutrients.oral.protein_g + micronutrients.parenteral.protein_g).toFixed(2),
    'g'
  ]);
  sheetData.push([
    'Carbohydrate',
    micronutrients.oral.carbohydrate_g.toFixed(2),
    micronutrients.parenteral.carbohydrate_g.toFixed(2),
    (micronutrients.oral.carbohydrate_g + micronutrients.parenteral.carbohydrate_g).toFixed(2),
    'g'
  ]);
  sheetData.push([
    'Fat',
    micronutrients.oral.fat_g.toFixed(2),
    micronutrients.parenteral.fat_g.toFixed(2),
    (micronutrients.oral.fat_g + micronutrients.parenteral.fat_g).toFixed(2),
    'g'
  ]);
  sheetData.push([]);

  // Sodium and Potassium in mEq
  sheetData.push(['ELECTROLYTES (mEq)']);
  sheetData.push(['Electrolyte', 'Oral/Enteral', 'Parenteral', 'Total']);
  sheetData.push([
    'Sodium (Na)',
    sodiumMeq.oral.toFixed(1),
    sodiumMeq.parenteral.toFixed(1),
    sodiumMeq.total.toFixed(1)
  ]);
  sheetData.push([
    'Potassium (K)',
    potassiumMeq.oral.toFixed(1),
    potassiumMeq.parenteral.toFixed(1),
    potassiumMeq.total.toFixed(1)
  ]);
  sheetData.push([]);

  // Oral/Enteral Micronutrients
  sheetData.push(['ORAL/ENTERAL MICRONUTRIENTS']);
  sheetData.push(['Composition', 'Unit', 'Amount']);
  
  // Fiber
  sheetData.push(['Fiber', 'g', micronutrients.oral.fiber_g.toFixed(2)]);
  
  // Electrolytes
  sheetData.push(['Sodium', 'mg', micronutrients.oral.sodium_mg.toFixed(2)]);
  sheetData.push(['Potassium', 'mg', micronutrients.oral.potassium_mg.toFixed(2)]);
  sheetData.push(['Chloride', 'mg', micronutrients.oral.chloride_mg.toFixed(2)]);
  
  // Fatty Acids
  sheetData.push(['Omega-3', 'mg', micronutrients.oral.omega3_mg.toFixed(2)]);
  sheetData.push(['Omega-6', 'mg', micronutrients.oral.omega6_mg.toFixed(2)]);
  
  // Vitamins
  sheetData.push(['Vitamin A', 'mcg', micronutrients.oral.vitamin_a_mcg.toFixed(2)]);
  sheetData.push(['Vitamin D3', 'mcg', micronutrients.oral.vitamin_d3_mcg.toFixed(2)]);
  sheetData.push(['Vitamin E', 'mcg', micronutrients.oral.vitamin_e_mcg.toFixed(2)]);
  sheetData.push(['Vitamin K', 'mcg', micronutrients.oral.vitamin_k_mcg.toFixed(2)]);
  sheetData.push(['Vitamin B1 (Thiamine)', 'mg', micronutrients.oral.vitamin_b1_mg.toFixed(2)]);
  sheetData.push(['Vitamin B2 (Riboflavin)', 'mg', micronutrients.oral.vitamin_b2_mg.toFixed(2)]);
  sheetData.push(['Vitamin B3 (Niacin)', 'mg', micronutrients.oral.vitamin_b3_mg.toFixed(2)]);
  sheetData.push(['Vitamin B5 (Pantothenic Acid)', 'mg', micronutrients.oral.vitamin_b5_mg.toFixed(2)]);
  sheetData.push(['Vitamin B6 (Pyridoxine)', 'mg', micronutrients.oral.vitamin_b6_mg.toFixed(2)]);
  sheetData.push(['Vitamin B12 (Cobalamin)', 'mcg', micronutrients.oral.vitamin_b12_mcg.toFixed(2)]);
  sheetData.push(['Folic Acid', 'mcg', micronutrients.oral.folic_acid_mcg.toFixed(2)]);
  sheetData.push(['Biotin', 'mcg', micronutrients.oral.biotin_mcg.toFixed(2)]);
  sheetData.push(['Choline', 'mg', micronutrients.oral.choline_mg.toFixed(2)]);
  sheetData.push(['Vitamin C (Ascorbic Acid)', 'mg', micronutrients.oral.vitamin_c_mg.toFixed(2)]);
  
  // Minerals
  sheetData.push(['Calcium', 'mg', micronutrients.oral.calcium_mg.toFixed(2)]);
  sheetData.push(['Phosphorus', 'mg', micronutrients.oral.phosphorus_mg.toFixed(2)]);
  sheetData.push(['Magnesium', 'mg', micronutrients.oral.magnesium_mg.toFixed(2)]);
  sheetData.push(['Iron', 'mg', micronutrients.oral.iron_mg.toFixed(2)]);
  sheetData.push(['Iodine', 'mcg', micronutrients.oral.iodine_mcg.toFixed(2)]);
  sheetData.push(['Zinc', 'mg', micronutrients.oral.zinc_mg.toFixed(2)]);
  sheetData.push(['Selenium', 'mcg', micronutrients.oral.selenium_mcg.toFixed(2)]);
  sheetData.push(['Manganese', 'mg', micronutrients.oral.manganese_mg.toFixed(2)]);
  sheetData.push(['Fluoride', 'mg', micronutrients.oral.fluoride_mg.toFixed(2)]);
  sheetData.push(['Chromium', 'mcg', micronutrients.oral.chromium_mcg.toFixed(2)]);
  sheetData.push(['Copper', 'mg', micronutrients.oral.copper_mg.toFixed(2)]);
  sheetData.push([]);

  // Parenteral Micronutrients
  sheetData.push(['PARENTERAL MICRONUTRIENTS']);
  sheetData.push(['Composition', 'Unit', 'Amount']);
  
  // Electrolytes
  sheetData.push(['Sodium', 'mmol', micronutrients.parenteral.sodium_mmol.toFixed(2)]);
  sheetData.push(['Potassium', 'mmol', micronutrients.parenteral.potassium_mmol.toFixed(2)]);
  sheetData.push(['Calcium', 'mmol', micronutrients.parenteral.calcium_mmol.toFixed(2)]);
  sheetData.push(['Chloride', 'mmol', micronutrients.parenteral.chloride_mmol.toFixed(2)]);
  sheetData.push(['Magnesium', 'mmol', micronutrients.parenteral.magnesium_mmol.toFixed(2)]);
  sheetData.push(['Phosphate', 'mmol', micronutrients.parenteral.phosphate_mmol.toFixed(2)]);
  
  // Other
  sheetData.push(['Lactate', 'mmol', micronutrients.parenteral.lactate_mmol.toFixed(2)]);
  sheetData.push(['Acetate', 'mEq', micronutrients.parenteral.acetate_meq.toFixed(2)]);
  sheetData.push(['Nitrogen', 'g', micronutrients.parenteral.nitrogen_g.toFixed(2)]);

  // Create worksheet
  const ws = XLSX.utils.aoa_to_sheet(sheetData);

  // Set column widths
  ws['!cols'] = [
    { wch: 35 }, // Column A - Composition/Label
    { wch: 15 }, // Column B - Unit/Oral
    { wch: 15 }, // Column C - Amount/Parenteral
    { wch: 15 }, // Column D - Total (if applicable)
    { wch: 10 }  // Column E - Unit (if applicable)
  ];

  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(wb, ws, 'Micronutrients');

  // Generate Excel file and trigger download
  XLSX.writeFile(wb, filename);
};
